// import { TOKEN_IN_ADVANCE_UPDATE_INTERVAL } from '@/config'

const STORAGE_LOGIN = 'ps-login'
const STORAGE_REFRESH = 'ps-refresh-token'
const STORAGE_ACCESS = 'ps-access-token'
const STORAGE_SESSION_ID = 'ps-session-id'
const STORAGE_TOKEN_LAST_REFRESHED = 'ps-token-last-refreshed'
const TREAT_TOKEN_AS_FRESH = 30_000

export interface StoredAuthInfo {
  refreshToken: string
  accessToken: string
  login: string
}

type OnTokenChanged = (_: StoredAuthInfo | null) => void
type OnSessionChanged = (_: string | null) => void
type TokenStorageConfig = {
  onTokenChanged: OnTokenChanged
  onSessionChanged: OnSessionChanged
}

export class TokenStorage {

  callbacks: {
    onTokenChanged: OnTokenChanged
    onSessionChanged: OnSessionChanged
  }

  constructor(
    {
      onTokenChanged,
      onSessionChanged,
    }: TokenStorageConfig,
  ) {
    this.callbacks = {
      onTokenChanged,
      onSessionChanged,
    }

    // event doesn't fire on same window, where change was made
    window.addEventListener('storage', (event) => {
      if (event.key === null) {
        // it occurs on storage.clear()
        this.callbacks.onTokenChanged(null)
        this.callbacks.onSessionChanged(null)
      } else if (event.key === STORAGE_ACCESS) {
        this.callbacks.onTokenChanged(this.loadAuthInfo())
      } else if (event.key === STORAGE_SESSION_ID) {
        this.callbacks.onSessionChanged(this.loadSessionId())
      }
    })
  }

  saveAuthInfo(info: StoredAuthInfo, updateRefreshed: boolean = true) {
    localStorage.setItem(STORAGE_LOGIN, info.login)
    localStorage.setItem(STORAGE_REFRESH, info.refreshToken)
    localStorage.setItem(STORAGE_ACCESS, info.accessToken)

    if (updateRefreshed) {
      localStorage.setItem(STORAGE_TOKEN_LAST_REFRESHED, String(Date.now()))
    }
  }

  loadAuthInfo(): StoredAuthInfo | null {
    const login = localStorage.getItem(STORAGE_LOGIN)
    const refreshToken = localStorage.getItem(STORAGE_REFRESH)
    const accessToken = localStorage.getItem(STORAGE_ACCESS)

    return (
      login && refreshToken && accessToken
        ? { login, refreshToken, accessToken }
        : null
    )
  }

  clearAuthInfo() {
    localStorage.removeItem(STORAGE_LOGIN)
    localStorage.removeItem(STORAGE_REFRESH)
    localStorage.removeItem(STORAGE_ACCESS)
    localStorage.removeItem(STORAGE_TOKEN_LAST_REFRESHED)
  }

  loadSessionId(): string | null {
    return localStorage.getItem(STORAGE_SESSION_ID)
  }

  saveSessionId(id: string) {
    localStorage.setItem(STORAGE_SESSION_ID, id)
  }

  isFreshToken(refreshToken: string | null, accessToken: string | null): boolean {
    if (!refreshToken || !accessToken) {
      return false
    }
    const storedInfo = this.loadAuthInfo()

    if (
      !storedInfo
      || storedInfo.refreshToken !== refreshToken
      || storedInfo.accessToken !== accessToken
    ) {
      return false
    }

    const lastRefreshed = Number(localStorage.getItem(STORAGE_TOKEN_LAST_REFRESHED) ?? 0)

    return (Date.now() - lastRefreshed < TREAT_TOKEN_AS_FRESH)
  }

}
